// pages/[...page].tsx
import { builder, BuilderComponent, useIsPreviewing } from '@builder.io/react';
import { BuilderContent } from '@builder.io/sdk';
import { Builder } from '@builder.io/sdk';
import { getAsyncProps } from '@builder.io/utils';
import { CARE_TYPES_OBJECT } from '@sly/core/constants/careTypes';
import { titleize } from '@sly/frontend/common/helpers/strings';
import { getStateAbbr } from '@sly/frontend/common/helpers/url';
import {
  getApiFilters,
  getSearchParams,
  getTocSeoLabel,
} from '@sly/frontend/react/search/helpers';
import Footer from '@sly/frontend/react/shared/components/Footer';
import HeaderContainer from '@sly/frontend/react/shared/components/Header/Header.container';
import { getEntityList } from '@sly/frontend/react/shared/helpers/getEntityList';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import slugify from 'slugify';
import { builderApiKey, googleMapsServerApiKey, isProd } from 'sly/config';
import { createApiClient, usePrefetch } from 'sly/services/api';
import apiEndpoints from 'sly/services/api/endpoints';
import { pageNames, useEvents } from 'sly/services/events';
import 'sly/builder-registry';

import Error from '../_error';

// Replace with your Public API Key
builder.init(builderApiKey);

// Define a function that fetches the Builder
// content for a given page
export const getServerSideProps: GetServerSideProps = async ({
  params,
  query,
}) => {
  const { api, store } = createApiClient(apiEndpoints);

  // Fetch the builder content for the given page
  const page = await builder
    .get('page', {
      userAttributes: {
        urlPath: `/${(params?.page as string[])?.join('/') || ''}`,
      },
    })
    .toPromise();

  const currentFilters: Record<string, string> = getSearchParams(query);

  let { state, city }: Record<string, string> = currentFilters || {};
  const { zip } = query;
  const apiFilters = getApiFilters(currentFilters);

  // Get city and state from zip using Google Geocoding API if zip is provided
  if (zip && !city && !state) {
    const { Client } = await import('@googlemaps/google-maps-services-js');
    const googleMapsClient = new Client({});
    try {
      const geocodingResponse = await googleMapsClient.geocode({
        params: {
          address: zip as string,
          key: googleMapsServerApiKey,
        },
      });

      if (geocodingResponse.data.results && geocodingResponse.data.results[0]) {
        const addressComponents =
          geocodingResponse.data.results[0].address_components;

        // Find city (checking multiple possible types)
        const cityComponent = addressComponents.find(
          (component: any) =>
            component.types.includes('locality') ||
            component.types.includes('sublocality') ||
            component.types.includes('postal_town') ||
            component.types.includes('administrative_area_level_3')
        );

        // Find state
        const stateComponent = addressComponents.find((component: any) =>
          component.types.includes('administrative_area_level_1')
        );

        if (cityComponent) {
          city = cityComponent.long_name;
        }
        if (stateComponent) {
          state = stateComponent.long_name;
        }
      }
    } catch (error) {
      console.error('Error fetching location data from zip:', error);
    }
  }

  try {
    await Promise.allSettled(
      [
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ((city && state) || zip) && api.getSearchPage.asAction(apiFilters),

        ((city && state) || zip) &&
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          api.getAgents.asAction({
            ...(city && state ? { 'filter[address]': `${city},${state}` } : {}),
            ...(zip ? { 'filter[zip]': zip } : {}),
            'page-size': 3,
          }),
        // Todo shouldBail is not working in next server
        currentFilters.city &&
          currentFilters.state &&
          currentFilters.toc &&
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          api.getGeoGuides.asAction(
            {
              city: currentFilters.city,
              state: currentFilters.state,
              toc: currentFilters.toc,
              include: 'curatedCommunities',
            },
            {
              encode: false,
              // shouldBail: currentFilters?.toc === CARE_TYPES_OBJECT.seniorLiving,
            }
          ),
      ]
        .filter(Boolean)
        .map(store.dispatch)
    );
  } catch (e) {
    console.log('error', e);
    return {
      notFound: true,
    };
  }

  try {
    await getAsyncProps(page, {
      async ReviewsSectionWrapper(props: any) {
        await store.dispatch(
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          api.getAllReviews.asAction({
            'filter[slug]': props.slugs,
            include: ['searchResource', 'searchResource.gallery.images'],
          })
        );
        return {
          ...props,
        };
      },
      async ExploreByCommunity(props: any) {
        props?.serverRender &&
          (await getEntityList(
            props,
            null,
            api,
            store,
            props?.entity === 'communities'
          ));
      },
      async ExpertHelpCta(props: any) {
        await store.dispatch(
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          api.getAgent.asAction({
            id: props.agentSlug,
          })
        );
      },
    });
  } catch (error) {
    console.log('error', error);
  }

  // Return the page content as props
  return {
    props: {
      page: page || null,
      initialApiState: store.getState(),
      city: city || null,
      state: state || null,
    },
    notFound: !page && !(Builder.isEditing || Builder.isPreviewing),
  };
};

// Define the Page component
export default function Page({
  page,
  city: initialCity,
  state: initialState,
}: {
  page: BuilderContent | null;
  city: string;
  state: string;
}) {
  const router = useRouter();
  const isPreviewing = useIsPreviewing();

  const { events } = useEvents();

  const { toc: care, city, state, zip } = router.query;

  const currentFilters: Record<string, string> = getSearchParams(router.query);
  const apiFilters = getApiFilters(currentFilters);

  const { requestInfo: geoGuideRequestInfo } = usePrefetch(
    'getGeoGuides',
    {
      city: currentFilters.city,
      state: currentFilters.state,
      toc: currentFilters.toc,
      include: 'curatedCommunities',
    },
    {
      encode: false,
      shouldBail:
        care === CARE_TYPES_OBJECT.seniorLiving ||
        !currentFilters.city ||
        !currentFilters.state ||
        !currentFilters.toc,
    }
  );

  const { requestInfo: requestResult } = usePrefetch(
    'getSearchPage',
    apiFilters,
    {
      shouldBail: !zip && !(city && state),
    }
  );

  const { requestInfo: requestInfoAgents } = usePrefetch(
    'getAgents',
    {
      ...(initialCity && initialState
        ? { 'filter[address]': `${initialCity},${initialState}` }
        : {}),
      ...(zip ? { 'filter[zip]': zip } : {}),
      'page-size': 3,
    },
    {
      shouldBail: !zip && !(initialCity && initialState),
    }
  );

  const geoGuide = geoGuideRequestInfo?.normalized?.[0] || {};

  useEffect(() => {
    if (page && !isPreviewing) {
      events.page(pageNames.Builder, {});
    }
  }, [page, isPreviewing, events]);

  if (!page && !isPreviewing) {
    return <Error statusCode={404} />;
  }

  // If the page content is available, render
  // the BuilderComponent with the page content
  return (
    <>
      <Head>
        <title>{page?.data?.title}</title>
        <meta name='description' content={page?.data?.description} />
        {!page?.data?.index && (
          <meta name='robots' content='noindex,nofollow' />
        )}
        {isProd && (
          <script
            id='tagmanager'
            defer
            dangerouslySetInnerHTML={{
              __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','GTM-5888W7H');`,
            }}
          />
        )}
      </Head>
      {!page?.data?.hideHeader && <HeaderContainer />}
      {/* Render the Builder page */}
      <BuilderComponent
        model='page'
        content={page || undefined}
        data={{
          tocLabel: getTocSeoLabel(currentFilters?.toc),
          stateAbbr: getStateAbbr(titleize(currentFilters?.state)),
          cityLabel: titleize(currentFilters?.city) || initialCity,
          stateLabel: titleize(currentFilters?.state) || initialState,
          slugCity:
            currentFilters?.city ||
            slugify(initialCity || '', {
              replacement: '-',
              lower: true,
              strict: true,
            }),
          slugState:
            currentFilters?.state ||
            slugify(initialState || '', {
              replacement: '-',
              lower: true,
              strict: true,
            }),
          geoGuide,
          search: requestResult?.normalized,
          agents: requestInfoAgents?.normalized,
        }}
      />
      {!page?.data?.hideFooter && <Footer />}
      {isProd && (
        <noscript>
          <iframe
            src='https://www.googletagmanager.com/ns.html?id=GTM-5888W7H'
            height='0'
            width='0'
            style={{
              display: 'none',
              visibility: 'hidden',
            }}
          ></iframe>
        </noscript>
      )}
    </>
  );
}
